import React from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import {
  CheckCircle as AccountedIcon,
  PersonOff as NotAccountedIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import { PersonStatus } from '../../Types/Incident';
import StatusActionButton from './StatusActionButton';
import NotCheckedIndicator from '../Elements/NotCheckedIndicator';
import { HEADCOUNT_STATUS_VISUALS } from '../../Utils/headcountStatus';

interface StatusActionGroupProps {
  currentStatus: PersonStatus;
  onStatusChange: (status: PersonStatus) => void;
}

const btnConfig: {
  status: 'accounted' | 'notAccounted';
  label: string;
  icon: React.ReactElement;
  activeBg: string;
  activeBorder: string;
  activeColor: string;
  inverted?: boolean;
}[] = [
  {
    status: 'accounted',
    label: 'Accounted',
    icon: <AccountedIcon />,
    activeBg: HEADCOUNT_STATUS_VISUALS.accounted.bg,
    activeBorder: HEADCOUNT_STATUS_VISUALS.accounted.color,
    activeColor: HEADCOUNT_STATUS_VISUALS.accounted.color,
  },
  {
    status: 'notAccounted',
    label: 'Not Accounted',
    icon: <NotAccountedIcon />,
    activeBg: HEADCOUNT_STATUS_VISUALS.notAccounted.bg,
    activeBorder: appColors.status.error,
    activeColor: HEADCOUNT_STATUS_VISUALS.notAccounted.color,
  },
];

const StatusActionGroup: React.FC<StatusActionGroupProps> = ({ currentStatus, onStatusChange }) => {
  const showUndo = currentStatus !== 'notChecked';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {currentStatus === 'notChecked' && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <NotCheckedIndicator />
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr)) 56px',
            sm: '144px 144px 72px',
          },
          gap: 1,
          alignItems: 'center',
        }}
      >
      {btnConfig.map((btn) => (
        <StatusActionButton
          key={btn.status}
          label={btn.label}
          icon={btn.icon}
          isActive={currentStatus === btn.status}
          activeBg={btn.activeBg}
          activeBorder={btn.activeBorder}
          activeColor={btn.activeColor}
          inverted={btn.inverted}
          onClick={() => onStatusChange(btn.status)}
        />
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {showUndo ? (
          <Tooltip title="Reset status to Not Checked" arrow>
            <Button
              size="small"
              startIcon={<UndoIcon sx={{ fontSize: 16 }} />}
              onClick={() => onStatusChange('notChecked')}
              sx={{
                minWidth: 'auto',
                height: 36,
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '0.75rem',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                px: 1,
                color: appColors.text.caption,
                border: `2px solid ${appColors.border.default}`,
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: appColors.surface.inactive,
                  borderColor: appColors.text.placeholder,
                },
              }}
            >
              Undo
            </Button>
          </Tooltip>
        ) : (
          <Box sx={{ width: 56 }} />
        )}
      </Box>
      </Box>
    </Box>
  );
};

export default StatusActionGroup;
